import { useEffect, useRef } from "react";

/**
 * Re-runs `refresh` whenever the tab regains focus or becomes visible again.
 *
 * Exists because a backgrounded browser tab's service worker can show an OS
 * notification for a push but has no access to the Redux store, so any live
 * update that arrives while the tab isn't focused never reaches the app.
 * Refetching on focus closes that gap: whatever was missed while backgrounded
 * is picked up the moment the user looks at the screen again.
 *
 * Deliberately NOT an interval. This is a correctness net for screens that are
 * left open — a counter tablet showing the dine-in floor, a diner's table —
 * not a polling loop, and it costs exactly one request per return to the
 * screen rather than one every N seconds forever.
 *
 * `refresh` is held in a ref so passing an inline arrow (the normal case)
 * doesn't tear down and re-add the listeners on every render.
 */
export function useLiveSync(enabled: boolean, refresh: () => void): void {
  const refreshRef = useRef(refresh);

  useEffect(() => {
    refreshRef.current = refresh;
  });

  useEffect(() => {
    if (!enabled) return;

    const resync = () => {
      if (document.visibilityState === "visible") refreshRef.current();
    };

    document.addEventListener("visibilitychange", resync);
    window.addEventListener("focus", resync);
    return () => {
      document.removeEventListener("visibilitychange", resync);
      window.removeEventListener("focus", resync);
    };
  }, [enabled]);
}

export default useLiveSync;
